<template>
  <PVProgressSpinner
    v-bind="$attrs"
    :data-testid="dataTestid"
    :stroke-width="strokeWidth"
    :class="spinnerClasses"
    :style="spinnerColorStyle"
    :aria-label="label"
    role="status"
  />
</template>

<script lang="ts">
/**
 * PrimeVue `ProgressSpinner` with standard sizing.
 *
 * Why we override `--p-progressspinner-color-*`:
 * The generated preset still ships placeholder `progressspinner` tokens (red/blue/green/yellow).
 * Prime paints the first animation frame from `color-one`, so the spinner looks “wrong” (e.g. red)
 * until Product defines real component tokens. Standalone usage defaults to `--p-primary-color`;
 * pass `severity` / `variant` (e.g. from `StandardButton`) to match button foreground tokens.
 */
export default {
  name: 'StandardSpinner',
  compatConfig: { MODE: 3 },
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { ButtonSeverity, ButtonSize, ButtonVariant } from '@/types/StandardButtonTypes';
import { getButtonSpinnerBackground } from '@/utils/mapStandardButtonToPVStyle';

const props = withDefaults(
  defineProps<{
    size?: ButtonSize;
    severity?: ButtonSeverity;
    variant?: ButtonVariant;
    label?: string;
    dataTestid?: string;
  }>(),
  {
    size: ButtonSize.NORMAL,
    variant: ButtonVariant.SOLID,
    label: 'Loading...',
  },
);

const strokeWidth = computed((): string => (props.size === ButtonSize.SMALL ? '3' : '2'));

const spinnerClasses = computed(
  (): Record<string, boolean> => ({
    'standard-spinner': true,
    'standard-spinner--sm': props.size === ButtonSize.SMALL,
  }),
);

const spinnerColorStyle = computed((): Record<string, string> | undefined => {
  if (props.severity == null) {
    return undefined;
  }

  return {
    '--standard-spinner-stroke-color': getButtonSpinnerBackground(props.severity, props.variant),
  };
});
</script>

<style scoped lang="scss">
.standard-spinner {
  --standard-spinner-stroke-color: var(--p-primary-color);
  --p-progressspinner-color-one: var(--standard-spinner-stroke-color);
  --p-progressspinner-color-two: var(--standard-spinner-stroke-color);
  --p-progressspinner-color-three: var(--standard-spinner-stroke-color);
  --p-progressspinner-color-four: var(--standard-spinner-stroke-color);

  display: inline-flex;
  flex-shrink: 0;
  line-height: 1;
  vertical-align: middle;

  &.p-progressspinner {
    width: 2rem;
    height: 2rem;
  }

  &--sm.p-progressspinner {
    width: 1rem;
    height: 1rem;
  }
}
</style>
